import {
  OUTSIDE_TEMPERATURE_THRESHOLDS,
  PressureUnit,
  TIRE_DELIMITER,
  TIRE_PRESSURE_THRESHOLD,
  TirePressureAccuracy,
  TirePressureStatus,
  TirePressureWairningLamp,
} from '@di-shared/features/diag/tire/const';
import {GetTireRecommendedData} from '@di-shared/api/di/__generated';

export function convertPressure(pressure: number, pressureUnit: PressureUnit) {
  switch (pressureUnit) {
    case PressureUnit.KPA:
      return Math.round(pressure * 5 * 0.145038);
    case PressureUnit.BAR:
      return Math.round((pressure / 10) * 14.5038);
    case PressureUnit.PSI:
    default:
      return pressure;
  }
}

export function getPressureUnit(firstUnit: PressureUnit) {
  switch (firstUnit) {
    case PressureUnit.KPA:
      return 'kPa';
    case PressureUnit.BAR:
      return 'bar';
    case PressureUnit.PSI:
    default:
      return 'psi';
  }
}

export function getTireStatus(myTirePressure: number, selectedTirePsi: number | undefined, tirePressureLamp: TirePressureWairningLamp) {
  if (tirePressureLamp === TirePressureWairningLamp.ON) {
    return TirePressureStatus.WARNING;
  }
  if (!selectedTirePsi) {
    return TirePressureStatus.INBOUND;
  }
  const lowPressureThreshold = selectedTirePsi * TIRE_PRESSURE_THRESHOLD.LOW_PRESSURE_MULTIPLIER;
  const highPressureThreshold = selectedTirePsi + TIRE_PRESSURE_THRESHOLD.HIGH_PRESSURE_ADDITION;
  if (myTirePressure <= lowPressureThreshold) {
    return TirePressureStatus.LOWER;
  }
  if (myTirePressure > highPressureThreshold) {
    return TirePressureStatus.HIGER;
  }
  return TirePressureStatus.INBOUND;
}

export function getTirePressureAccuracyByTemperature(outSideTemperature: number) {
  if (outSideTemperature === OUTSIDE_TEMPERATURE_THRESHOLDS.UNKNOWN) {
    return TirePressureAccuracy.UNKNOWN;
  }
  if (outSideTemperature < OUTSIDE_TEMPERATURE_THRESHOLDS.LOW) {
    return TirePressureAccuracy.LOW;
  }
  if (OUTSIDE_TEMPERATURE_THRESHOLDS.LOW <= outSideTemperature && outSideTemperature < OUTSIDE_TEMPERATURE_THRESHOLDS.HIGH) {
    return TirePressureAccuracy.ACCURATE;
  }
  if (OUTSIDE_TEMPERATURE_THRESHOLDS.HIGH <= outSideTemperature) {
    return TirePressureAccuracy.HIGH;
  }
  return TirePressureAccuracy.ERROR;
}

export function splitTireName(tire: string) {
  const [frontTire, rearTire] = tire.split(TIRE_DELIMITER);
  return [frontTire, rearTire ? rearTire : frontTire];
}

export function getTirePsi(tireName: string, tirePsiList: GetTireRecommendedData[]) {
  return tirePsiList.find(({tire}) => tire === tireName);
}

export function getTirePsiFromServerFormat(serverFormattedTire: string, tirePsiList: GetTireRecommendedData[]) {
  const [frontTire, rearTire] = splitTireName(serverFormattedTire);
  const frontMatch = getTirePsi(frontTire, tirePsiList);
  const rearMatch = getTirePsi(rearTire, tirePsiList);
  return [frontMatch, rearMatch];
}

export function getIsRecommendedTireSingleSpec(tirePsiList: GetTireRecommendedData[]) {
  return tirePsiList.every((tire) => tire.frontPsi === tirePsiList[0].frontPsi && tire.rearPsi === tirePsiList[0].rearPsi);
}

export function getFrontTireBound(tireName: string, tirePsiList: GetTireRecommendedData[]) {
  return tirePsiList.find(({tire}) => tire === tireName)?.frontPsi;
}

export function getRearTireBound(tireName: string, tirePsiList: GetTireRecommendedData[]) {
  return tirePsiList.find(({tire}) => tire === tireName)?.rearPsi;
}
